import { BankAccountType, BankLink } from '@orbis-systems/orbis-api-middleware';
import { queryOptions } from '@tanstack/react-query';
import { filterActiveBankLinks } from '@/features/bank-links/utils/bank-link-filters';
import { normalizeBankLinksResponse } from '@/features/bank-links/utils/normalize-bank-links-response';
import { orbis, orbisMiddleware, queryClient } from '@/queries/api';

export type BankLinksQueryParams = {
    accountNumber?: string;
};

export type CreateBankLinkParams = {
    accountNumber: string;
    bankRoutingNumber: string;
    bankAccount: string;
    bankAccountOwnerName: string;
    bankAccountType: BankAccountType;
    nickname: string;
};

export type RenameBankLinkParams = {
    entryId: number;
    accountNumber: string;
    nickname: string;
};

export type CancelBankLinkParams = {
    entryId: number;
    accountNumber: string;
};

export type ConfirmBankLinkParams = {
    accountNumber: string;
    entryId: number;
    amount1: number;
    amount2: number;
};

export type IavCompleteParams = {
    accountNumber: string;
    publicToken: string;
    bankAccountId: string;
};

async function fetchBankLinks(accountNumber = ''): Promise<BankLink[]> {
    const response = await orbis.transfers.relationships.list(accountNumber);
    return filterActiveBankLinks(normalizeBankLinksResponse(response.data));
}

export const bankLinksQueries = {
    all: () => ['bank-links'] as const,
    list: (params: BankLinksQueryParams = {}) =>
        queryOptions<BankLink[]>({
            queryKey: [...bankLinksQueries.all(), 'list', params],
            queryFn: () => fetchBankLinks(params.accountNumber),
        }),
    tempIavKey: () =>
        queryOptions<string>({
            queryKey: [...bankLinksQueries.all(), 'temp-iav-key'],
            queryFn: () => orbisMiddleware.account.getTempIavKey(),
        }),
};

export const bankLinksMutations = {
    create: (params: CreateBankLinkParams) =>
        orbisMiddleware.account.createAchBankLink({
            ...params,
            confirmBankAccount: params.bankAccount,
        }),
    rename: ({ accountNumber, entryId, nickname }: RenameBankLinkParams) =>
        orbisMiddleware.account.renameBankLink(accountNumber, entryId, nickname),
    cancel: ({ accountNumber, entryId }: CancelBankLinkParams) =>
        orbisMiddleware.account.cancelBankLink(accountNumber, entryId),
    confirm: (params: ConfirmBankLinkParams) => orbisMiddleware.account.approveBankLink(params),
    iavComplete: ({ accountNumber, publicToken, bankAccountId }: IavCompleteParams) =>
        orbisMiddleware.account.iavComplete(accountNumber, publicToken, bankAccountId),
};

export function invalidateBankLinksQueries() {
    return queryClient.invalidateQueries({ queryKey: bankLinksQueries.all() });
}
